import { useSelector } from 'react-redux';

import { FILE_PATH_IN_REPORTS } from '../../../../../../../../shared/featureFlags/flagNames';
import {
  dependencyInfoOptionFormats,
  DependencyInfoOptions,
  dependencyInfoOptionsWithDescriptions,
} from '../../../../../../../../shared/reports';
import { window } from '../../../../../../../globals';
import { isFeatureEnabled } from '../../../../../../../helpers/features';
import useIsLicenseConcludedEnabled from '../../../../../../../hooks/features/useIsLicenseConcludedEnabled';
import * as reportGeneration from '../../../../../../../redux/reportGeneration';
import FilterCardCheckboxes from '../../../../../../generic/Filter/FilterCardCheckboxes';
import { ReportCategories } from '../../../utils';
import { reportTypeFormatMap } from '../../constants';
import type { SelectOption, SetState } from '../../../../../../../types';
import type { DependencyInfoOption, ReportCategory } from '../../../../shared/types';
import type { FormatSelection } from '../FormatSelect';

import styles from './Filter.module.less';

type Props = {
  format: FormatSelection;
  values: SelectOption[];
  category: ReportCategory;
  setValues: SetState<SelectOption[]>;
};

const DependencyMetadataCheckboxGroup = ({ format, values, category, setValues }: Props) => {
  const organizationId = window.user?.organization?.id;
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(organizationId as number);

  const isFilePathInReports = isFeatureEnabled(FILE_PATH_IN_REPORTS);
  const projectType = useSelector(reportGeneration.selectors.getProjectType);
  const selectedFormat = reportTypeFormatMap[format.value];
  const options = Object.entries(DependencyInfoOptions)
    .filter(([key]) => {
      if (key === 'Library' || key === 'NoticeFiles') {
        return false;
      }
      const applicableFormats = dependencyInfoOptionFormats[key as DependencyInfoOption];
      if (applicableFormats && !applicableFormats.has(selectedFormat)) {
        return false;
      }
      if (key === 'FilePath') {
        return isFilePathInReports;
      }
      if (key === 'Project') {
        return projectType === 'releaseGroup' && category === ReportCategories.Licensing;
      }

      if (key === 'ConcludedLicense' && !isLicenseConcludedEnabled) {
        return false;
      }

      const licensingOnlyDependencyMetadata = new Set(['IssueNotes', 'Source']);
      if (licensingOnlyDependencyMetadata.has(key)) {
        return category === ReportCategories.Licensing;
      }

      return true;
    })
    .map(([key, value]) => {
      const tooltip = (
        <div className={styles.tooltipText}>{dependencyInfoOptionsWithDescriptions[key as DependencyInfoOption]}</div>
      );

      return {
        label: value,
        value: key,
        tooltip,
      };
    });

  return (
    <div className="width-full" data-test-id="sbom-filters-dependency-metadata">
      <FilterCardCheckboxes label="Dependency Metadata" options={options} values={values} onChange={setValues} tooltipPlacement="left" />
    </div>
  );
};

export default DependencyMetadataCheckboxGroup;
